import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Component
// ============================================================================

/**
 * Gallery page intro: a centered heading and short blurb that open the page
 * below the hero/wave band, ahead of the tabbed photo gallery.
 */
export function GalleryIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-[40px] pb-0"
    >
      <div className="mx-auto px-3 sm:px-4 lg:px-6 text-center">
        <RichTextContent
          path="galleryintro.heading"
          label="Heading"
          defaultValue='<p>Explore <span data-type="field-token" data-field="property_name"></span></p>'
          as="h2"
          className="font-heading text-balance text-[clamp(1.75rem,6vw,2.5rem)] font-semibold uppercase tracking-wide text-[var(--vibe-color-primary)] leading-[1.1] mb-6"
        />
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-16 lg:px-8 text-center">
        <RichTextContent
          path="galleryintro.body"
          label="Body Text"
          defaultValue='<p>Browse our gallery to see the beautiful apartments and resort-style amenities at <span data-type="field-token" data-field="property_name"></span>. Discover why our community is the perfect place to call home.</p>'
          as="div"
          className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_p:last-child]:mb-0 [&_a]:text-primary [&_a]:underline"
        />
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryIntro',
  displayName: 'Gallery Intro',
  tokens: {
    'galleryintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Explore <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'galleryintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_gallery_blurb',
      defaultValue:
        '<p>Browse our gallery to see the beautiful apartments and resort-style amenities at <span data-type="field-token" data-field="property_name"></span>. Discover why our community is the perfect place to call home.</p>',
    },
  },
};

(GalleryIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default GalleryIntro;
